import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Popover,
  Typography,
  CircularProgress,
  type PopoverActions,
} from "@mui/material";
import { X as CloseIcon } from "lucide-react";
import {
  mainPopoverStyles,
  pageContainerStyles,
  cardHeaderRowStyles,
  cardTitleStyles,
  closeButtonStyles,
  fieldsContainerStyles,
  loadingContainerStyles,
  loaderContainerStyles,
} from "./styles";
import { RealtimeMenuItem } from "@/types/realtimeSpeech";
import { postRealtimeMenu } from "../../../axios/realtimeSpeech";
import { useAppStore } from "../../../stores/appStore";
import { DetailsConfirmationModal } from "./details-confirmation-modal/details-confirmation-modal";
import { useVideoModeAvailability } from "../../../hooks/useVideoModeAvailability";
import {
  RoleplayDropdownField,
  RoleplayFieldType,
} from "./roleplay-dropdown-field";
import {
  RoleplayModeToggle,
  type RoleplayMode,
} from "./roleplay-mode-toggle";
import { LiveSpeechSessionErrorModal } from "../../live-speech-session-error-modal/live-speech-session-error-modal";
import type { LiveSpeechSessionError } from "../../../types/liveSpeechSessionError";
import {
  isBrowserOffline,
  OFFLINE_SESSION_ERROR,
} from "../../../types/liveSpeechSessionError";
import { createMenuSelectionPipeline } from "../../../helpers/menuSelectionPipeline";
import {
  createAsyncRoleplaySession,
  getAsyncRoleplayErrorDetail,
  listAsyncRoleplaySessions,
} from "../../../axios/asyncRoleplay";
import { AsyncRoleplayResumeList } from "../../async-roleplay/async-roleplay-resume-list/async-roleplay-resume-list";
import {
  loadAsyncRoleplaySession,
  saveAsyncRoleplaySession,
} from "../../../utils/asyncRoleplayStorage";
import type { AsyncRoleplaySession } from "../../../types/asyncRoleplay";

interface MenuData {
  name: string;
  options: RealtimeMenuItem[];
}

interface RealtimeSpeechMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

type SubmittingMode = RoleplayMode | null;

type MenuState = {
  menus: MenuData[];
  selectedOptions: Record<string, string | undefined>;
  isLoading: boolean;
  isLastMenu: boolean;
  submittingMode: SubmittingMode;
  isSubmittingNext: boolean;
  submittingMenuName: string | null;
};

const getMenuFieldType = (name: string): RoleplayFieldType => {
  const lower = name.toLowerCase();
  if (lower.includes("persona")) return "persona";
  if (lower.includes("domain") || lower.includes("practice")) return "domain";
  if (lower.includes("scenario")) return "scenario";
  return "default";
};

const isModeMenu = (name: string) => {
  const lower = name.toLowerCase();
  return lower.includes("mode") || lower.includes("speech");
};

const getFieldLabel = (name: string, fieldType: RoleplayFieldType): string => {
  if (fieldType === "persona") return "Persona (HCP)";
  if (fieldType === "domain") return "Practice domain";
  if (fieldType === "scenario") return "Scenario";
  return name;
};

export const RealtimeSpeechMenu: React.FC<RealtimeSpeechMenuProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const [menuState, setMenuState] = useState<MenuState>({
    menus: [],
    selectedOptions: {},
    isLoading: false,
    isLastMenu: false,
    submittingMode: null,
    isSubmittingNext: false,
    submittingMenuName: null,
  });
  const [sessionError, setSessionError] =
    useState<LiveSpeechSessionError | null>(null);
  const {
    setSellingDialogue,
    setLiveSpeechMode,
    setRealtimePersona,
    setRealtimeMenuOptions,
    realtimeDetailsWindowText,
    setRealtimeDetailsWindowText,
    waitingForDetailsConfirmation,
    setWaitingForDetailsConfirmation,
    setAsyncRoleplayOpen,
    setAsyncRoleplaySessionId,
    setAsyncRoleplayRecordId,
    domain,
    focus,
    lessonDialogue,
    lessonDialogueMetadata,
  } = useAppStore();

  const [chooseForMeMetadata, setChooseForMeMetadata] = useState<Record<
    string,
    any
  > | null>(null);

  const initialFetchDoneRef = useRef(false);
  const goInFlightRef = useRef(false);
  const lastGoAudioOnlyRef = useRef(false);
  const lastGoModeRef = useRef<RoleplayMode>("audio");
  const popoverActionsRef = useRef<PopoverActions | null>(null);
  const chooseForMeMetadataRef = useRef(chooseForMeMetadata);
  const menuStateRef = useRef(menuState);

  const schedulePopoverPositionUpdate = useCallback(() => {
    requestAnimationFrame(() => {
      popoverActionsRef.current?.updatePosition();
    });
  }, []);
  const applySelectionResponseRef = useRef<(
    response: any,
    requestId: number,
  ) => void>(() => undefined);
  const clearSelectionSubmittingRef = useRef<() => void>(() => undefined);

  const selectionPipelineRef = useRef(
    createMenuSelectionPipeline<Record<string, any>, any>({
      save: postRealtimeMenu,
      onResponse: (response, requestId) => {
        applySelectionResponseRef.current(response, requestId);
      },
      onError: (error, requestId) => {
        console.error("Error submitting menu:", error);
        if (requestId === selectionPipelineRef.current.getRequestId()) {
          clearSelectionSubmittingRef.current();
        }
      },
    }),
  );

  chooseForMeMetadataRef.current = chooseForMeMetadata;
  menuStateRef.current = menuState;

  const { isVideoModeAvailable, isCheckingNetwork, videoDisabledReason } =
    useVideoModeAvailability(open);

  const resetStateAndClose = useCallback(
    (opts?: { keepDetailsText?: boolean }) => {
      selectionPipelineRef.current.invalidate();
      goInFlightRef.current = false;
      setChooseForMeMetadata(null);
      if (!opts?.keepDetailsText) {
        setRealtimeDetailsWindowText(null);
      }
      setWaitingForDetailsConfirmation(false);
      setMenuState({
        menus: [],
        selectedOptions: {},
        isLoading: false,
        isLastMenu: false,
        submittingMode: null,
        isSubmittingNext: false,
        submittingMenuName: null,
      });
      initialFetchDoneRef.current = false;
      onClose();
    },
    [onClose, setRealtimeDetailsWindowText, setWaitingForDetailsConfirmation],
  );

  const showBackendMenuError = useCallback(
    (message: string) => {
      resetStateAndClose();
      setSessionError({
        code: "week_limit_exceeded",
        message,
        retryable: false,
        canFallbackAudioOnly: false,
      });
    },
    [resetStateAndClose],
  );

  const clearSelectionSubmitting = useCallback(() => {
    setMenuState((prev) => ({
      ...prev,
      isSubmittingNext: false,
      submittingMenuName: null,
    }));
  }, []);
  clearSelectionSubmittingRef.current = clearSelectionSubmitting;

  const applySelectionResponse = useCallback(
    (response: any, requestId: number) => {
      if (requestId !== selectionPipelineRef.current.getRequestId()) {
        return;
      }

      if (response?.error) {
        showBackendMenuError(String(response.error));
        return;
      }

      if (response?.metadata && typeof response.metadata === "object") {
        const metadata = response.metadata;
        const detailsText =
          metadata.details_window_text || metadata.details_window_tex;

        if (detailsText && typeof detailsText === "string") {
          const {
            details_window_text,
            details_window_tex,
            ...metadataWithoutDetails
          } = metadata;
          if (requestId !== selectionPipelineRef.current.getRequestId()) {
            return;
          }
          setChooseForMeMetadata(metadataWithoutDetails);
          setRealtimeDetailsWindowText(detailsText);
          setWaitingForDetailsConfirmation(true);
          clearSelectionSubmitting();
          return;
        }
      }

      if (
        !response ||
        !response.options ||
        (Array.isArray(response.options) && response.options.length === 0)
      ) {
        setMenuState((prev) => {
          if (requestId !== selectionPipelineRef.current.getRequestId()) {
            return prev;
          }
          return {
            ...prev,
            isLastMenu: true,
            isSubmittingNext: false,
            submittingMenuName: null,
          };
        });
        return;
      }

      const menuData: MenuData = {
        name: response.name || "",
        options: response.options || [],
      };

      setMenuState((prev) => {
        if (requestId !== selectionPipelineRef.current.getRequestId()) {
          return prev;
        }
        return {
          ...prev,
          menus: [...prev.menus, menuData],
          isLastMenu: false,
          isSubmittingNext: false,
          submittingMenuName: null,
        };
      });
    },
    [
      clearSelectionSubmitting,
      setRealtimeDetailsWindowText,
      setWaitingForDetailsConfirmation,
      showBackendMenuError,
    ],
  );
  applySelectionResponseRef.current = applySelectionResponse;

  const enqueueSelectionSave = useCallback(
    (payload: Record<string, any>) => {
      setRealtimeMenuOptions(payload);
      return selectionPipelineRef.current.enqueue(payload);
    },
    [setRealtimeMenuOptions],
  );

  const fetchMenu = useCallback(
    async (data: Record<string, any>) => {
      const requestId = selectionPipelineRef.current.getRequestId();
      setMenuState((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await postRealtimeMenu(data);

        if (requestId !== selectionPipelineRef.current.getRequestId()) {
          return;
        }

        if (response?.error) {
          showBackendMenuError(String(response.error));
          return;
        }

        if (
          !response ||
          !response.options ||
          (Array.isArray(response.options) && response.options.length === 0)
        ) {
          setMenuState((prev) => ({
            ...prev,
            isLoading: false,
            isLastMenu: true,
            isSubmittingNext: false,
            submittingMenuName: null,
          }));
          return;
        }

        const menuData: MenuData = {
          name: response.name || "",
          options: response.options || [],
        };

        setMenuState((prev) => ({
          ...prev,
          menus: [...prev.menus, menuData],
          isLoading: false,
          isLastMenu: false,
          isSubmittingNext: false,
          submittingMenuName: null,
        }));
      } catch (error) {
        console.error("Error fetching menu:", error);
        if (requestId === selectionPipelineRef.current.getRequestId()) {
          setMenuState((prev) => ({
            ...prev,
            isLoading: false,
            isSubmittingNext: false,
            submittingMenuName: null,
          }));
        }
      }
    },
    [showBackendMenuError],
  );

  useEffect(() => {
    if (open && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      fetchMenu({});
    }
  }, [open, fetchMenu]);

  const handleChooseOption = (
    menu: MenuData,
    option: RealtimeMenuItem,
  ) => {
    if (goInFlightRef.current || menuStateRef.current.submittingMode) {
      return;
    }

    const prev = menuStateRef.current;
    const menuIndex = prev.menus.findIndex((m) => m.name === menu.name);
    if (menuIndex === -1) return;

    if (chooseForMeMetadataRef.current !== null) {
      chooseForMeMetadataRef.current = null;
      setChooseForMeMetadata(null);
      setRealtimeDetailsWindowText(null);
      setWaitingForDetailsConfirmation(false);
    }

    const newSelectedOptions: Record<string, string | undefined> = {
      ...prev.selectedOptions,
      [menu.name]: option.text,
    };

    const trimmedMenus = prev.menus.slice(0, menuIndex + 1);
    for (let i = menuIndex + 1; i < prev.menus.length; i++) {
      delete newSelectedOptions[prev.menus[i].name];
    }

    const dataToSend: Record<string, any> = {};
    for (const [key, value] of Object.entries(newSelectedOptions)) {
      if (value) dataToSend[key] = value;
    }

    const nextState: MenuState = {
      ...prev,
      menus: trimmedMenus,
      selectedOptions: newSelectedOptions,
      isLastMenu: false,
      isSubmittingNext: true,
      submittingMenuName: menu.name,
    };
    menuStateRef.current = nextState;
    setMenuState(nextState);
    void enqueueSelectionSave(dataToSend);
  };

  const normalizePersona = (p: any) => {
    if (!p || typeof p !== "object") return p;
    const avatar =
      p.avatar ||
      p.avatar_url ||
      p.avatarUrl ||
      p.image ||
      p.image_url ||
      p.imageUrl ||
      p.photo ||
      p.photo_url ||
      p.photoUrl;
    if (avatar && !p.avatar) {
      return { ...p, avatar };
    }
    return p;
  };

  const buildFinalSettingsPayload = (opts?: { forAsync?: boolean }) => {
    const { menus, selectedOptions } = menuStateRef.current;
    const metadata = chooseForMeMetadataRef.current;
    const dataToSend: Record<string, any> = metadata ? { ...metadata } : {};

    if (!metadata) {
      for (const [key, value] of Object.entries(selectedOptions)) {
        if (value) dataToSend[key] = value;
      }
    }

    const findMenuByKeywords = (keywords: string[]) => {
      return menus.find((menu) =>
        keywords.some((keyword) =>
          menu.name.toLowerCase().includes(keyword.toLowerCase()),
        ),
      );
    };

    let resolvedRealtimeMode: string | null = null;
    if (metadata) {
      if (metadata.persona) {
        const personaValue = metadata.persona;
        if (typeof personaValue === "string") {
          setRealtimePersona({
            text: personaValue,
            description: "",
            avatar: "",
          } as any);
        } else if (typeof personaValue === "object" && personaValue !== null) {
          setRealtimePersona(normalizePersona(personaValue) as any);
        }
      }

      if (metadata.realtime_mode) {
        resolvedRealtimeMode = metadata.realtime_mode;
      } else if (metadata.mode) {
        resolvedRealtimeMode = metadata.mode;
      } else {
        resolvedRealtimeMode = "selling_dialogue";
      }
    } else {
      const modeMenu = findMenuByKeywords(["mode", "speech"]) || menus[0];

      if (modeMenu) {
        const modeValue = selectedOptions[modeMenu.name];
        resolvedRealtimeMode = modeValue || "selling_dialogue";
      } else {
        resolvedRealtimeMode = "selling_dialogue";
      }

      const personaMenu = findMenuByKeywords(["persona"]);
      if (personaMenu && selectedOptions[personaMenu.name]) {
        const selectedPersonaText = selectedOptions[personaMenu.name];
        const selectedPersona = personaMenu.options.find(
          (opt) => opt.text === selectedPersonaText,
        );
        if (selectedPersona) {
          setRealtimePersona(normalizePersona(selectedPersona) as any);
        }
      }
    }

    if (resolvedRealtimeMode) {
      dataToSend.realtime_mode = resolvedRealtimeMode;
      setLiveSpeechMode(resolvedRealtimeMode);
    }

    if (opts?.forAsync) {
      // Async is an interaction mode, not a new assessment category.
      // Prefer an explicit lesson context when present; otherwise selling.
      const isLessonContext =
        Boolean(dataToSend.lesson_dialogue) ||
        Boolean(lessonDialogue) ||
        Boolean(lessonDialogueMetadata);

      if (isLessonContext) {
        dataToSend.lesson_dialogue = true;
        delete dataToSend.selling_dialogue;
        if (!dataToSend.metadata && lessonDialogueMetadata) {
          dataToSend.metadata = lessonDialogueMetadata;
        }
      } else {
        dataToSend.selling_dialogue = true;
        delete dataToSend.lesson_dialogue;
      }
    }

    return dataToSend;
  };

  const openAsyncSession = useCallback(
    (session: AsyncRoleplaySession) => {
      setAsyncRoleplaySessionId(session.session_id);
      setAsyncRoleplayRecordId(session.record_id);
      const { realtimePersona } = useAppStore.getState();
      const previous = loadAsyncRoleplaySession();
      const personaAvatar =
        realtimePersona?.avatar ||
        realtimePersona?.avatar_url ||
        realtimePersona?.avatarUrl ||
        (previous?.sessionId === session.session_id
          ? previous.personaAvatar
          : null) ||
        null;
      saveAsyncRoleplaySession({
        sessionId: session.session_id,
        recordId: session.record_id,
        domain: domain || "",
        focus: focus || "",
        windowTitle: session.window_title,
        persona: session.persona,
        personaAvatar,
      });
      setAsyncRoleplayOpen(true);
      resetStateAndClose();
    },
    [
      domain,
      focus,
      resetStateAndClose,
      setAsyncRoleplayOpen,
      setAsyncRoleplayRecordId,
      setAsyncRoleplaySessionId,
    ],
  );

  const handleGoLive = async (audioOnly: boolean) => {
    if (goInFlightRef.current || menuStateRef.current.submittingMode) {
      return;
    }

    goInFlightRef.current = true;
    lastGoAudioOnlyRef.current = audioOnly;
    lastGoModeRef.current = audioOnly ? "audio" : "video";
    setMenuState((prev) => ({
      ...prev,
      submittingMode: audioOnly ? "audio" : "video",
    }));

    try {
      await selectionPipelineRef.current.waitForIdle();

      const dataToSend = buildFinalSettingsPayload();
      dataToSend.audio_only = audioOnly;
      setRealtimeMenuOptions(dataToSend);

      let response: any;
      try {
        response = await postRealtimeMenu(dataToSend);
      } catch (error) {
        console.error("Error submitting final menu:", error);
        setMenuState((prev) => ({ ...prev, submittingMode: null }));
        setSessionError(
          isBrowserOffline()
            ? { ...OFFLINE_SESSION_ERROR, canFallbackAudioOnly: false }
            : {
                code: "realtime_error",
                message: "Failed to start roleplay. Please try again.",
                retryable: true,
                canFallbackAudioOnly: false,
              },
        );
        return;
      }

      if (response?.error) {
        showBackendMenuError(String(response.error));
        return;
      }

      setSellingDialogue(true);
      resetStateAndClose({ keepDetailsText: true });
    } finally {
      goInFlightRef.current = false;
    }
  };

  const handleGoAsync = async () => {
    if (goInFlightRef.current || menuStateRef.current.submittingMode) {
      return;
    }

    goInFlightRef.current = true;
    lastGoModeRef.current = "async";
    setMenuState((prev) => ({
      ...prev,
      submittingMode: "async",
    }));

    try {
      await selectionPipelineRef.current.waitForIdle();

      const dataToSend = buildFinalSettingsPayload({ forAsync: true });
      // Keep dialogue mode; do not set realtime_mode to "async".
      delete dataToSend.audio_only;
      // coach_me is not supported by the async roleplay router.
      if (dataToSend.realtime_mode === "coach_me") {
        setMenuState((prev) => ({ ...prev, submittingMode: null }));
        setSessionError({
          code: "realtime_error",
          message: "Voice Messaging mode does not support coaching (coach_me).",
          retryable: false,
          canFallbackAudioOnly: false,
        });
        return;
      }
      if (
        dataToSend.lesson_dialogue &&
        (!dataToSend.metadata || typeof dataToSend.metadata !== "object")
      ) {
        setMenuState((prev) => ({ ...prev, submittingMode: null }));
        setSessionError({
          code: "realtime_error",
          message:
            "Lesson async roleplay requires original lesson metadata.",
          retryable: false,
          canFallbackAudioOnly: false,
        });
        return;
      }
      setRealtimeMenuOptions(dataToSend);

      let session;
      try {
        session = await createAsyncRoleplaySession(dataToSend);
      } catch (error: any) {
        const status = error?.response?.status;
        // Create is not idempotent: recover from uncertain create via list.
        if (!status || status >= 500 || status === 0) {
          try {
            const sessions = await listAsyncRoleplaySessions();
            const newest = [...sessions].sort(
              (a, b) => (b.created_at ?? 0) - (a.created_at ?? 0),
            )[0];
            if (newest?.session_id) {
              session = newest;
            }
          } catch {
            // fall through to error UI
          }
        }

        if (!session) {
          console.error("Error creating async roleplay session:", error);
          setMenuState((prev) => ({ ...prev, submittingMode: null }));
          const detail = getAsyncRoleplayErrorDetail(error);
          setSessionError(
            isBrowserOffline()
              ? { ...OFFLINE_SESSION_ERROR, canFallbackAudioOnly: false }
              : {
                  code: "realtime_error",
                  message:
                    detail ||
                    "Failed to start Voice Messaging. Please try again.",
                  retryable: true,
                  canFallbackAudioOnly: false,
                },
          );
          return;
        }
      }

      if (!session?.session_id || !session?.record_id) {
        setMenuState((prev) => ({ ...prev, submittingMode: null }));
        setSessionError({
          code: "realtime_error",
          message: "Failed to start Voice Messaging. Please try again.",
          retryable: true,
          canFallbackAudioOnly: false,
        });
        return;
      }

      openAsyncSession(session);
    } finally {
      goInFlightRef.current = false;
    }
  };

  const {
    menus,
    selectedOptions,
    isLoading,
    isLastMenu,
    submittingMode,
    isSubmittingNext,
    submittingMenuName,
  } = menuState;

  // Menus / resume list load async after open — refresh Popover layout.
  useEffect(() => {
    if (!open) return;
    schedulePopoverPositionUpdate();
  }, [open, menus.length, isLoading, schedulePopoverPositionUpdate]);

  const selectionMenus = menus.filter((menu) => !isModeMenu(menu.name));

  const allSelected =
    (isLastMenu &&
      (selectionMenus.length === 0 ||
        selectionMenus.every((menu) => !!selectedOptions[menu.name]))) ||
    (chooseForMeMetadata !== null && !waitingForDetailsConfirmation);

  const isModeSubmitting = submittingMode !== null;

  const handleModeSelect = (mode: RoleplayMode) => {
    if (allSelected && !isModeSubmitting) {
      if (mode === "video" && !isVideoModeAvailable) return;
      if (mode === "async") {
        void handleGoAsync();
        return;
      }
      void handleGoLive(mode === "audio");
    }
  };

  if (!open && !sessionError) return null;

  const showInitialLoading =
    selectionMenus.length === 0 && isLoading && !isModeSubmitting;

  return (
    <>
      {open && (
        <Popover
          action={popoverActionsRef}
          anchorEl={anchorEl}
          open={open}
          onClose={() => resetStateAndClose()}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={mainPopoverStyles}
        >
          <Box sx={pageContainerStyles}>
            <DetailsConfirmationModal
              open={waitingForDetailsConfirmation}
              detailsWindowText={realtimeDetailsWindowText}
              selectedOptions={selectedOptions}
              onClose={() => {
                setWaitingForDetailsConfirmation(false);
                setRealtimeDetailsWindowText(null);
                setChooseForMeMetadata(null);
                resetStateAndClose();
              }}
              onConfirm={() => {
                setWaitingForDetailsConfirmation(false);
              }}
            />

            <Box sx={cardHeaderRowStyles}>
              <Typography sx={cardTitleStyles}>Start Roleplay</Typography>
              <Box
                component="button"
                type="button"
                onClick={() => resetStateAndClose()}
                sx={closeButtonStyles}
                aria-label="Close roleplay menu"
              >
                <CloseIcon size={18} />
              </Box>
            </Box>

            {showInitialLoading ? (
              <Box sx={loadingContainerStyles}>
                <CircularProgress size={28} sx={{ color: "#7B49C1" }} />
                <Typography variant="body2" color="text.secondary">
                  Loading menu...
                </Typography>
              </Box>
            ) : (
              <Box sx={fieldsContainerStyles}>
                <AsyncRoleplayResumeList
                  open={open}
                  disabled={isModeSubmitting}
                  onResume={openAsyncSession}
                  onContentChange={schedulePopoverPositionUpdate}
                />

                {selectionMenus.map((menu) => {
                  const fieldType = getMenuFieldType(menu.name);
                  const isFieldLoading =
                    isSubmittingNext && submittingMenuName === menu.name;

                  return (
                    <RoleplayDropdownField
                      key={menu.name}
                      label={getFieldLabel(menu.name, fieldType)}
                      fieldType={fieldType}
                      options={menu.options}
                      value={selectedOptions[menu.name] || ""}
                      placeholder="Select an option"
                      disabled={isModeSubmitting}
                      loading={isFieldLoading}
                      onChange={(option) => handleChooseOption(menu, option)}
                    />
                  );
                })}

                {selectionMenus.length > 0 && isLoading && (
                  <Box sx={loaderContainerStyles}>
                    <CircularProgress size={20} sx={{ color: "#7B49C1" }} />
                  </Box>
                )}

                <RoleplayModeToggle
                  canStart={allSelected && !isModeSubmitting}
                  videoDisabled={!isVideoModeAvailable || isCheckingNetwork}
                  videoDisabledReason={videoDisabledReason}
                  submittingMode={submittingMode}
                  onSelect={handleModeSelect}
                />
              </Box>
            )}
          </Box>
        </Popover>
      )}

      <LiveSpeechSessionErrorModal
        error={sessionError}
        onRetry={
          sessionError?.retryable
            ? () => {
                setSessionError(null);
                if (lastGoModeRef.current === "async") {
                  void handleGoAsync();
                } else {
                  void handleGoLive(lastGoAudioOnlyRef.current);
                }
              }
            : undefined
        }
        onExit={() => {
          setSessionError(null);
          resetStateAndClose();
        }}
      />
    </>
  );
};
